import { useState, useCallback, useEffect, useRef } from 'react'
import { useConnect, useDisconnect, useAccount, useSignMessage } from 'wagmi'
import { reconnect, getAccount } from 'wagmi/actions'
import { useWeb3Modal } from '@web3modal/wagmi/react'
import { wagmiConfig } from '@/config/wagmi'
import { authApi } from '@/services/api/auth'
import { isSuccess } from '@/services/types/common'
import { useUserStore } from '@/stores/user'
import { api } from '@/services/api/client'
import { getInviteRef, clearInviteRef } from '@/utils/inviteRef'
import { useRegisterConfirmStore } from '@/stores/registerConfirm'
import i18n from '@/i18n'

export function useWalletAuth() {
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isPending = useRef(false)
  const isAuthenticating = useRef(false)

  const { connectAsync, connectors } = useConnect()
  const { disconnectAsync } = useDisconnect()
  const { address, isConnected } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const { login, logout, token: storeToken } = useUserStore()
  const { open: openModal, close: closeModal } = useWeb3Modal()

  const doLogin = useCallback(async (walletAddress: string): Promise<boolean> => {
    if (isAuthenticating.current) return false
    isAuthenticating.current = true
    setIsConnecting(true)
    setError(null)
    try {
      const nonceResp = await authApi.nonce(walletAddress)
      if (!isSuccess(nonceResp)) throw new Error(nonceResp.msg)

      const { nonce, registered, inviteCodeRequired } = nonceResp.data

      // 首次注册：先让用户确认邀请码再签名。
      //
      // 顺序很关键 —— 签名即建号、推荐人当场绑死且无法自助更换，
      // 所以确认必须发生在 signMessageAsync 之前；等 connect 报错再提示，
      // 用户已经白签了一次，而且关系可能已经绑错了。
      // registered 由 /auth/nonce 返回（后端查 user_infos），老用户不会被打扰。
      let inviteCode = getInviteRef() || undefined
      // 开关关闭且本来就没有邀请码 —— 这是「直接注册顶号」的场景，没有任何关系要确认，
      // 弹窗只会是干扰。其余两种情况都要确认：
      //   · 开关开启           —— 必须绑推荐人，绑错不可逆
      //   · 开关关闭但带了邀请码 —— 仍会绑推荐人，同样要核对
      const needConfirm = !registered && (inviteCodeRequired || !!inviteCode)
      if (needConfirm) {
        const confirmed = await useRegisterConfirmStore.getState().request({
          inviteCode: inviteCode ?? '',
          inviteCodeRequired,
        })
        if (confirmed === null) {
          // 用户取消注册：不是错误，静默结束，也不要记进 lastAuthAttemptAddr 之外的错误态
          return false
        }
        inviteCode = confirmed || undefined
      }

      const signature = await signMessageAsync({
        message: `Sign in to USD1 Staking\nNonce: ${nonce}`,
      })
      const connectResp = await authApi.connect({
        address: walletAddress,
        signature,
        nonce,
        inviteCode,
      })
      if (!isSuccess(connectResp)) throw new Error(connectResp.msg)

      const { token, user, expiresAt } = connectResp.data
      api.setAuthToken(token)
      login(user, token, expiresAt)
      clearInviteRef()
      if (isPending.current) closeModal()
      lastAuthAttemptAddr.current = null
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('auth.connectFailed')
      // 抑制 wagmi 内部 "Connector not connected" 错误（移动端自动重连残留状态触发）
      if (message.includes('Connector not connected')) {
        try { await disconnectAsync() } catch {}
        // 清理 wagmi 持久化的 stale 连接状态，防止下次刷新重现
        try { localStorage.removeItem('wagmi.store') } catch {}
      } else {
        setError(message)
      }
      return false
    } finally {
      setIsConnecting(false)
      isAuthenticating.current = false
      isPending.current = false
    }
  }, [signMessageAsync, login, closeModal, disconnectAsync])

  const lastAuthAttemptAddr = useRef<string | null>(null)

  // 自动触发登录：Web3Modal 流程、自动连接（TokenPocket）、地址切换
  useEffect(() => {
    if (!isConnected || !address) {
      lastAuthAttemptAddr.current = null
      return
    }

    if (isPending.current) {
      doLogin(address)
      return
    }

    const { token: storedToken, user: storedUser } = useUserStore.getState()

    if (storedToken && storedUser && storedUser.walletAddress.toLowerCase() === address.toLowerCase()) {
      return
    }

    // 已经尝试过此地址但失败了（用户拒绝签名等），不重复弹出
    if (lastAuthAttemptAddr.current === address.toLowerCase()) {
      return
    }

    // 地址切换：先清除旧的 auth 状态
    if (storedToken && storedUser) {
      logout()
    }

    lastAuthAttemptAddr.current = address.toLowerCase()
    doLogin(address)
  }, [isConnected, address, storeToken, doLogin, logout])

  const authenticate = useCallback(async () => {
    setError(null)

    // 获取 wagmi 实时状态（React hook 值可能滞后）
    const account = getAccount(wagmiConfig)

    // 防御：stale auth 状态（token 有效但 wagmi 未连接）→ 清除后走正常连接流程
    if (!account.isConnected) {
      const { token: t } = useUserStore.getState()
      if (t) logout()
    } else if (account.address) {
      const success = await doLogin(account.address)
      if (success) return
      // 登录失败（残留 connector 不可用）— doLogin 已自动断开并清理 stale 状态
      // 继续往下走重新连接流程
    }

    // 优先尝试注入式钱包（MetaMask、TokenPocket dApp 浏览器等）
    if (window.ethereum) {
      setIsConnecting(true)
      try {
        const injectedConnector = connectors.find(c => c.type === 'injected')
        if (injectedConnector) {
          const result = await connectAsync({ connector: injectedConnector })
          const walletAddress = result.accounts[0]
          if (walletAddress) {
            await doLogin(walletAddress)
            return
          }
        }
      } catch {
        // connectAsync 失败 — dApp 浏览器可能已连接但 wagmi 未同步
        // 直接通过 provider 获取账户作为兜底
        try {
          const accounts = await window.ethereum!.request({ method: 'eth_requestAccounts' }) as string[]
          if (accounts?.[0]) {
            // 先同步 wagmi 状态，再登录（顺序执行，避免竞态条件）
            await reconnect(wagmiConfig).catch(() => {})
            await doLogin(accounts[0])
            return
          }
        } catch {}
        setIsConnecting(false)
      }
    }

    // 无注入式钱包 — 打开 Web3Modal 扫码连接
    isPending.current = true
    try {
      openModal()
    } catch {
      setError(i18n.t('auth.installWallet'))
    }
  }, [connectors, connectAsync, doLogin, openModal])

  const disconnect = useCallback(async () => {
    await disconnectAsync()
    logout()
    setError(null)
  }, [disconnectAsync, logout])

  const clearError = useCallback(() => setError(null), [])

  return {
    authenticate,
    disconnect,
    isConnecting,
    error,
    clearError,
  }
}
